import { Info } from 'lucide-react';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  cn,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  withTooltip,
} from '@crossnokaye/ux-common';

import { ScheduleEvent } from '@repo/sdk/models/components';

import { getInitialDefrostSchedule } from '@/features/equipment-browser/equipment-details/details-form/equipment-details-form.helpers';
import { Inline } from '@/ux-common/components/inline/inline';

import { DEFROST_SCHEDULE_TABLE_HEADERS } from './defrost-schedule.constant';
import { DefrostScheduleRow } from './defrost-schedule-row';

const InfoTooltip = withTooltip(Info);

type DefrostScheduleTableProps = {
  events: ScheduleEvent[];
};

export const DefrostScheduleTable = ({ events }: DefrostScheduleTableProps) => {
  const form = useFormContext();
  // Use watch() instead of getValues() to get default values for unregistered fields
  const data = form.watch();
  const eventsCount = data?.defrostSchedule?.length ?? 0;

  const initialEvents = useMemo(() => {
    return getInitialDefrostSchedule(events);
  }, [events]);

  return (
    <div className="overflow-x-auto">
      <Table className="table-auto w-full [&>tbody>tr]:h-8 text-sm">
        <TableHeader>
          <TableRow>
            {DEFROST_SCHEDULE_TABLE_HEADERS.map((header) => (
              <TableHead key={header.key} className={cn(header.width && header.width)}>
                {header.infoTooltip ? (
                  <Inline className="inline-row-xs items-center">
                    {header.label}
                    <InfoTooltip
                      tooltipContent={header.infoTooltip}
                      className="glyph glyph-sm text-muted-foreground"
                      aria-label={`More information about ${header.label}`}
                    />
                  </Inline>
                ) : (
                  header.label
                )}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {eventsCount > 0 ? (
            Array.from({ length: eventsCount }).map((_, index) => (
              <DefrostScheduleRow key={index} index={index} initialEvent={initialEvents?.[index]} />
            ))
          ) : (
            <TableRow key="no-events">
              <TableCell colSpan={DEFROST_SCHEDULE_TABLE_HEADERS.length}>No events found</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
